import type { GridFilterModel } from "@mui/x-data-grid-premium";
import { useInfiniteQuery } from "@tanstack/react-query";
import { ResourceEnum } from "src/constants/constants.ts";
import { getExonnApi } from "src/exonn-api/exonn-api.tsx";
import type {
  TransferPanelGetDataParams,
  ValidRowModel,
} from "src/components/Form/panels/TransferPanel/TransferPanel.tsx";

export const useTransferListRecords = ({
  is_assigned,
  is_excluded,
  filter,
  resource,
  formResource,
  locale,
  params,
}: {
  is_assigned: 0 | 1;
  is_excluded: 0 | 1;
  filter?: GridFilterModel;
  resource: string;
  formResource: string;
  locale: string;
  params: {
    id: string;
  };
}) => {
  return useInfiniteQuery({
    queryKey: [
      "form-transfer-modal",
      resource,
      formResource,
      locale,
      params,
      is_assigned,
      is_excluded,
      filter,
    ],
    refetchOnMount: true,
    staleTime: 0, // Data is considered stale immediately
    gcTime: 0,
    queryFn: async ({ pageParam = 0 }) => {
      return await getGetData({
        resource,
        formResource,
        params,
        locale,
      })({
        is_assigned,
        is_excluded,
        filter,
        page: {
          number: pageParam,
          size: 2000,
        },
      });
    },
    initialPageParam: 1,
    getNextPageParam: (response, allPages, lastPageParam) => {
      if (lastPageParam + 1 > response.meta.last_page) {
        return undefined;
      }
      return lastPageParam + 1;
    },
  });
};

const getGetData = ({
  params,
  resource: transferResource,
  formResource: mainResource,
  locale,
}: {
  params: {
    id: string;
  };
  resource: string;
  formResource: string;
  locale: string;
}): GetDataFn<any> => {
  if (
    transferResource === ResourceEnum.Variant &&
    mainResource === ResourceEnum.Manufacturer
  ) {
    return async (innerParams) => {
      const api = getExonnApi();
      const response = await api.manufacturer.getProducts(
        {
          ...innerParams,
          ...params,
        },
        null,
        {
          headers: {
            "X-Locale": locale,
          },
        },
      );

      return response.data;
    };
  }
  if (transferResource === ResourceEnum.DeliveryService) {
    return async (innerParams) => {
      const api = getExonnApi();
      const response = await api.deliveryMethod.getDeliveryServices({
        ...params,
        ...innerParams,
      });

      return response.data;
    };
  }
  if (transferResource === ResourceEnum.Variant) {
    return async (innerParams) => {
      const api = getExonnApi();
      const response = await api.system.productTransferList.getRows(
        {
          ...params,
          type: mainResource,
          ...innerParams,
        },
        null,
        {
          headers: {
            "X-Locale": locale,
          },
        },
      );

      return {
        data: response.data.data.map((resource) => ({
          ...resource,
          title: resource.title || resource?.parentProductItem?.title,
        })),
        meta: response.data.meta,
      };
    };
  }
  if (transferResource === ResourceEnum.Country) {
    return async (innerParams) => {
      const api = getExonnApi();
      const response = await api.system.transferList.getRows<"country">(
        {
          ...params,
          ...innerParams,
          type: mainResource,
          transfer_type: transferResource,
        },
        null,
        {
          headers: {
            "X-Locale": locale,
          },
        },
      );

      return {
        data: response.data.data,
        meta: response.data.meta,
      };
    };
  }
  return async (innerParams) => {
    const api = getExonnApi();
    const response = await api.system.transferList.getRows<"product-group">(
      {
        ...innerParams,
        ...params,
        type: mainResource,
        transfer_type: transferResource,
      },
      null,
      {
        headers: {
          "X-Locale": locale,
        },
      },
    );

    return response.data;
  };
};

type GetDataFn<Row extends ValidRowModel = ValidRowModel> = (
  params: TransferPanelGetDataParams,
) => Promise<{
  data: Row[];
  meta: {
    last_page: number;
    total: number;
  };
}>;
